import { rootStore } from "../../redux/store";
import { vsprintf } from "sprintf-js";
import { setErrorState } from "../../redux/error_banner/actions";
import { sortCategories } from "./constants";
import {
    CEM_FAILED_TEXT,
    CEM_GENERATED_TEXT,
    NEEDS_DATA_TEXT,
    NEEDS_REVIEW_TEXT,
    NO_STATUS_TEXT,
    PATHOLOGY_ENTRY_REQUESTED_TEXT,
    INELIGIBLE_TEXT,
    planStageEnum,
    READY_FOR_PATHOLOGY_ENTRY_TEXT,
} from "../__shared__/RequirementsModal/constants";
import {
    API_ENDPOINT,
    API_VERSION,
    ASCENDING,
    httpMethods,
    SUCCESS,
    clientErrorMessages,
} from "../../constants";

export const CELL_PADDING = "px-6 first:pl-12 last:pr-12";

const alignHeader = {
    left: "justify-start",
    center: "justify-center",
    right: "justify-end",
};

const alignCell = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
};

export const headerAlignClass = (col) => alignHeader[col.meta?.align ?? "left"];
export const cellAlignClass = (col) => alignCell[col.meta?.align ?? "left"];

export async function getUserPatientsMetadata(input) {
    let endpoint = vsprintf("%s/api/%s/tplan_store/user/%s/patients/metadata", [
        API_ENDPOINT,
        API_VERSION,
        input.useruuid,
    ]);
    return await fetch(endpoint, {
        method: httpMethods.GET,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${input.token}`,
        },
    })
        .then((response) => response.json())
        .then((json) => {
            if (json.message === SUCCESS && json.payload.status === SUCCESS) {
                return json.payload;
            }
            throw new Error(clientErrorMessages.TPLAN_RETRIEVE_FAILED);
        })
        .catch((e) => {
            rootStore.dispatch(setErrorState(true, String(e)));
            return null;
        });
}

export async function getNotifications(input) {
    let endpoint = vsprintf("%s/api/%s/tplan_store/notifications/plan/%s", [
        API_ENDPOINT,
        API_VERSION,
        input.planuuid,
    ]);
    return await fetch(endpoint, {
        method: httpMethods.GET,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${input.token}`,
        },
    })
        .then((response) => response.json())
        .then((json) => {
            if (json.message === SUCCESS && json.payload.status === SUCCESS) {
                return json.payload;
            } else {
                return null;
            }
        })
        .catch(() => null);
}

// NO_STATUS (0) sorts last among plan stages; derive the placeholder from the
// enum's current max so a newly added stage can't collide with it.
const NO_STATUS_SORT_VALUE = Math.max(...Object.values(planStageEnum)) + 1;

const compareValues = (valueA, valueB, order) => {
    if (valueA < valueB) {
        return order === ASCENDING ? -1 : 1;
    } else if (valueA > valueB) {
        return order === ASCENDING ? 1 : -1;
    } else {
        return 0;
    }
};

export function sortPatientData(patA, patB, category, order) {
    switch (category) {
        case sortCategories.SEARCH_SCORE: {
            return compareValues(patA.score, patB.score, order);
        }
        case sortCategories.FLAG: {
            const stepA = patA?.flagNote || "";
            const stepB = patB?.flagNote || "";
            return compareValues(stepA, stepB, order);
        }

        case sortCategories.NAME: {
            return compareValues(
                patA.fullName.toLowerCase(),
                patB.fullName.toLowerCase(),
                order
            );
        }

        case sortCategories.DATE: {
            const creationDateA = patA.plans[0].creationDate;
            const creationDateB = patB.plans[0].creationDate;
            const planCreationTimeA = patA.plans[0].planCreationTime;
            const planCreationTimeB = patB.plans[0].planCreationTime;

            if (creationDateA !== creationDateB) {
                return compareValues(creationDateA, creationDateB, order);
            } else {
                return compareValues(
                    planCreationTimeA,
                    planCreationTimeB,
                    order
                );
            }
        }

        case sortCategories.ID: {
            return compareValues(
                patA.patientId.toLowerCase(),
                patB.patientId.toLowerCase(),
                order
            );
        }

        case sortCategories.PLAN_STATUS: {
            const planStageA = patA.planStage;
            const planStageB = patB.planStage;
            const creationDateA = patA.plans[0].creationDate;
            const creationDateB = patB.plans[0].creationDate;
            const stepA = patA.plans[0].SavedData.PlanCreationStep;
            const stepB = patB.plans[0].SavedData.PlanCreationStep;

            // Custom sort order: all known stages ascending, with NO_STATUS (0) sorted last
            if (planStageA !== planStageB) {
                // Convert plan stages to sortable values according to desired order
                const getSortValue = (stage) => {
                    const stage_num = Number(stage);
                    return stage_num === 0 ? NO_STATUS_SORT_VALUE : stage_num;
                };

                const sortValueA = getSortValue(planStageA);
                const sortValueB = getSortValue(planStageB);

                return compareValues(sortValueA, sortValueB, order);
            } else if (creationDateA !== creationDateB) {
                return compareValues(creationDateA, creationDateB, order);
            } else {
                return compareValues(stepA, stepB, order);
            }
        }

        default: {
            return 0;
        }
    }
}

export function sortPlanData(planA, planB, category, order) {
    switch (category) {
        case sortCategories.DATE: {
            const d1 = Date.parse(planA.creationDate);
            const d2 = Date.parse(planB.creationDate);
            return compareValues(d1, d2, order);
        }

        case sortCategories.ID: {
            return compareValues(planA.planId, planB.planId, order);
        }

        default: {
            return 0;
        }
    }
}

export function planStageToText(planStage) {
    switch (Number(planStage)) {
        case planStageEnum.NEEDS_REVIEW:
            return NEEDS_REVIEW_TEXT;
        case planStageEnum.NEEDS_DATA:
            return NEEDS_DATA_TEXT;
        case planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS:
            return PATHOLOGY_ENTRY_REQUESTED_TEXT;
        case planStageEnum.CEM_GENERATED:
            return CEM_GENERATED_TEXT;
        case planStageEnum.CEM_FAILED:
            return CEM_FAILED_TEXT;
        case planStageEnum.INELIGIBLE:
            return INELIGIBLE_TEXT;
        case planStageEnum.READY_FOR_PATHOLOGY_ENTRY:
            return READY_FOR_PATHOLOGY_ENTRY_TEXT;
        default:
            return NO_STATUS_TEXT;
    }
}

// an in-progress plan with a CEM can only download the patient report PDF
// an in-progress plan without a CEM cannot be downloaded yet (CEM not generated)
export function getPlanDownloadInfo(plan) {
    const inProgress = !plan?.SavedData?.PlanCreationCompleteFlag;
    const hasCEM = plan?.SavedData?.CPMForLUTDisplayURI != null;

    return {
        isDownloadDisabled: inProgress && !hasCEM,
        pdfOnly: inProgress && hasCEM,
    };
}

export const PATIENT_LIST_VIEWS = [
    { id: "all", label: "All", planStages: null },
    {
        id: "in_process",
        label: "In Process",
        planStages: [
            planStageEnum.NEEDS_DATA,
            planStageEnum.PATHOLOGY_ENTRY_IN_PROGRESS,
            planStageEnum.READY_FOR_PATHOLOGY_ENTRY,
        ],
    },
    {
        id: "ready_for_generation",
        label: "Ready for Generation",
        planStages: [planStageEnum.NEEDS_REVIEW],
    },
    {
        id: "generated",
        label: "Generated",
        planStages: [planStageEnum.CEM_GENERATED],
    },
    {
        id: "ineligible",
        label: "Ineligible",
        planStages: [planStageEnum.INELIGIBLE],
    },
];

export function filterPatientsByView(patients, viewId) {
    const view = PATIENT_LIST_VIEWS.find((view) => view.id === viewId);

    if (!view || view.planStages === null) {
        return patients;
    }

    return patients.filter((p) =>
        view.planStages.includes(Number(p.MetaData?.PlanStage))
    );
}
